<template>
  <div class="image-embed">
    <!-- Loading state (embed loading) -->
    <div v-if="embedLoader.isLoading.value" class="image-embed__loading">
      <v-progress-circular indeterminate size="64" width="4" color="primary" />
      <span class="image-embed__loading-text">Loading...</span>
    </div>

    <!-- The load failed: what it means to a visitor, never the API's sentence -->
    <PlayerRefusal
      v-else-if="embedLoader.failure.value"
      :failure="embedLoader.failure.value"
      :can-retry="!!props.embedId"
      @retry="handleRetry"
    />

    <!-- Billing gate: owner out of credits -->
    <PlayerUnavailable v-else-if="isBlocked" />

    <!-- Player -->
    <ImagePlayer
      ref="imagePlayerRef"
      v-else-if="playerConfig"
      :config="playerConfig"
      :theme="theme"
      :primary-color="primaryColor"
      :logo-url="logoUrl"
      :logo-position="logoPosition"
      :logo-click-url="logoClickUrl"
      :enable-asset-link="enableAssetLink"
      @item-change="handleItemChange"
      @layout-change="$emit('layoutChange', $event)"
      @zoom-change="$emit('zoomChange', $event)"
      @pan-change="$emit('panChange', $event)"
      @slideshow-toggle="$emit('slideshowToggle', $event)"
      @slideshow-pause="$emit('slideshowPause', $event)"
      @fullscreen-change="$emit('fullscreenChange', $event)"
      @image-load="$emit('imageLoad', $event)"
      @image-error="$emit('imageError', $event)"
      @download="$emit('download', $event)"
      @error="$emit('error', $event)"
      @displayed="handleDisplayed"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRef, onMounted } from 'vue'
import type { PropType } from 'vue'
import type { Embed } from '../../types/embed'
import type { ImageAssetListItem, ImagePlaybackInfo } from '../../types/asset'
import type { ImagePlayerConfig, PanZoomState, ImageLayout } from '../types'
import type { PlayerError } from '../../types/base'
import type { CollectionItem } from '../../types/player'
import { useEmbedLoader } from '../../common/composables/useEmbedLoader'
import PlayerRefusal from '../../common/components/PlayerRefusal.vue'
import { usePlaybackInfoCache } from '../../common/composables/usePlaybackInfoCache'
import { endOncePerLoad, isSingleAsset } from '../../common/stillEnd'
import { useImagePlayer } from '../composables/useImagePlayer'
import ImagePlayer from './ImagePlayer.vue'
import PlayerUnavailable from '../../common/components/PlayerUnavailable.vue'
import { createUUID } from '../../types/base'

const props = defineProps({
  /** Embed ID to load */
  embedId: {
    type: String,
    default: undefined,
  },
  /** Embed data (if already loaded) */
  embed: {
    type: Object as PropType<Embed<ImageAssetListItem>>,
    default: undefined,
  },
  /** Viewer ID for analytics */
  viewerId: {
    type: String,
    default: undefined,
  },
  /** Initial layout mode */
  layout: {
    type: String as PropType<ImageLayout>,
    default: 'carousel',
  },
  /** Enable zoom */
  enableZoom: {
    type: Boolean,
    default: true,
  },
  /** Max zoom level */
  maxZoom: {
    type: Number,
    default: 4,
  },
  /** Enable fullscreen */
  enableFullscreen: {
    type: Boolean,
    default: true,
  },
  /** Enable download */
  enableDownload: {
    type: Boolean,
    default: false,
  },
  /** Enable slideshow */
  enableSlideshow: {
    type: Boolean,
    default: true,
  },
  /** Slideshow duration in seconds */
  slideshowDuration: {
    type: Number,
    default: 5,
  },
  /** Show captions */
  showCaptions: {
    type: Boolean,
    default: true,
  },
  /** Prefetch adjacent images */
  prefetchAdjacent: {
    type: Boolean,
    default: true,
  },
  /** Player theme */
  theme: {
    type: String as PropType<'dark' | 'light'>,
    default: 'dark',
  },
  /** Primary color override (CSS color value, e.g. hex) */
  primaryColor: {
    type: String,
    default: undefined,
  },
  /** Workspace logo URL to overlay on the player */
  logoUrl: {
    type: String,
    default: undefined,
  },
  /** Logo position over the player */
  logoPosition: {
    type: String as PropType<'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' | 'control_bar'>,
    default: undefined,
  },
  /** Optional URL the logo links to when clicked */
  logoClickUrl: {
    type: String,
    default: undefined,
  },
  /** Enable the in-app link to the asset page (Mediasbox only) */
  enableAssetLink: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  embedLoad: [embed: Embed<ImageAssetListItem>]
  itemChange: [index: number, item: CollectionItem<ImageAssetListItem>]
  layoutChange: [layout: ImageLayout]
  zoomChange: [state: PanZoomState]
  panChange: [panX: number, panY: number]
  slideshowToggle: [active: boolean]
  slideshowPause: [paused: boolean]
  fullscreenChange: [isFullscreen: boolean]
  imageLoad: [assetId: string]
  imageError: [assetId: string, error: PlayerError]
  download: [assetId: string]
  error: [error: PlayerError]
  /** An image played alone is on screen, which is its end (`../../common/stillEnd.ts`). */
  ended: []
}>()

// Embed loader
const embedIdRef = toRef(props, 'embedId')
const embedPropRef = toRef(props, 'embed')

const embedLoader = useEmbedLoader<ImageAssetListItem>({
  embedId: embedIdRef,
  embed: embedPropRef,
  autoLoad: true,
})

// Playback info cache
const playbackInfoCache = usePlaybackInfoCache<ImagePlaybackInfo>({
  playbackToken: computed(() => embedLoader.embed.value?.playbackToken),
  itemToken: computed(() => embedLoader.embed.value?.itemToken),
  collectionPlaybackId: computed(() => embedLoader.embed.value?.collectionPlaybackId),
  sessionId: computed(() => embedLoader.embed.value?.sessionId),
})

// Current item index for tracking
const currentIndex = ref(0)

// Player config - derived from embed
const playerConfig = computed<ImagePlayerConfig | null>(() => {
  const embed = embedLoader.embed.value
  if (!embed) return null

  return {
    uid: embed.uid,
    title: embed.name,
    items: embed.items.map((item, index) => ({
      asset: item.asset,
      position: index,
      customTitle: item.customTitle,
      customDescription: item.customDescription,
    })),
    currentIndex: currentIndex.value,
    viewerId: createUUID(props.viewerId || 'viewer'),
    layout: props.layout,
    enableZoom: props.enableZoom,
    maxZoom: props.maxZoom,
    enableFullscreen: props.enableFullscreen,
    enableDownload: props.enableDownload,
    enableSlideshow: props.enableSlideshow,
    slideshowDuration: props.slideshowDuration,
    showCaptions: props.showCaptions,
    callbacks: {
      onPlaybackInfoNeeded: fetchPlaybackInfo,
    },
  }
})

/**
 * Fetch playback info for an asset (called by ImagePlayer)
 */
async function fetchPlaybackInfo(assetUid: string): Promise<ImagePlaybackInfo> {
  return playbackInfoCache.fetchOne(assetUid)
}

// Public billing gate: owner out of credits — show a neutral "unavailable" state.
// The current image's playback info is prefetched, so its `blocked` flag is known.
const isBlocked = computed(() => {
  const currentItem = embedLoader.embed.value?.items[currentIndex.value]
  return currentItem ? playbackInfoCache.get(currentItem.asset.uid)?.blocked === true : false
})

/**
 * Handle item change - prefetch adjacent images
 */
function handleItemChange(index: number, item: CollectionItem<ImageAssetListItem>): void {
  currentIndex.value = index
  emit('itemChange', index, item)

  // Prefetch adjacent images
  if (props.prefetchAdjacent && embedLoader.embed.value) {
    const items = embedLoader.embed.value.items
    const adjacentUids: string[] = []

    if (index > 0) {
      adjacentUids.push(items[index - 1].asset.uid)
    }
    if (index < items.length - 1) {
      adjacentUids.push(items[index + 1].asset.uid)
    }

    // Prefetch in background (don't await)
    if (adjacentUids.length > 0) {
      playbackInfoCache.fetchBatch(adjacentUids).catch(() => {
        // Silently ignore prefetch errors
      })
    }
  }
}

// An image has no playback to finish: played alone, it has ended once it is shown — once per
// load, whatever redraws it afterwards. A gallery of several has no single end and says nothing.
const reachEnd = endOncePerLoad(() => emit('ended'))

function handleDisplayed(): void {
  const embed = embedLoader.embed.value
  if (embed && isSingleAsset(embed)) reachEnd(embed)
}

/**
 * Handle retry button click
 */
function handleRetry(): void {
  if (props.embedId) {
    embedLoader.load(props.embedId)
  }
}

// Watch for embed load and prefetch first item
watch(
  () => embedLoader.embed.value,
  async (embed) => {
    if (embed) {
      emit('embedLoad', embed)

      // Prefetch first item's playback info
      if (embed.items.length > 0) {
        try {
          await playbackInfoCache.fetchOne(embed.items[0].asset.uid)
        } catch {
          // Error will be handled by the player
        }
      }
    }
  },
  { immediate: true }
)

const imagePlayerRef = ref<InstanceType<typeof ImagePlayer> | null>(null)

// Expose methods for external control
defineExpose({
  embedLoader,
  playbackInfoCache,
  reload: () => props.embedId && embedLoader.load(props.embedId),
  goToIndex: (index: number) => imagePlayerRef.value?.goToIndex(index),
})
</script>

<style lang="scss" scoped>
.image-embed {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  &__loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  &__loading-text {
    font-size: 13px;
    color: rgba(var(--v-theme-on-surface), 0.75);
    max-width: 300px;
    text-align: center;
  }
}
</style>
